// ============================================================================
// ACCOUNTS (Chart of Accounts) ENDPOINTS
// ============================================================================

import { buildEndpoint, buildEndpointWithParams, buildEndpointWithQuery } from './base.endpoints';
import type { ID } from '../types';

export const accountTypeEndpoints = {
  list: () => buildEndpoint('/account-types'),
  getById: (id: ID) => buildEndpointWithParams('/account-types/:id', { id }),
} as const;

export const reportClassEndpoints = {
  list: () => buildEndpoint('/report-classes'),
  getById: (id: ID) => buildEndpointWithParams('/report-classes/:id', { id }),
} as const;

export const accountSubtypeEndpoints = {
  list: () => buildEndpoint('/account-subtypes'),
  getById: (id: ID) => buildEndpointWithParams('/account-subtypes/:id', { id }),
} as const;

const GROUP_BASE = '/coa-groups';
export const coaGroupEndpoints = {
  list: () => buildEndpoint(GROUP_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${GROUP_BASE}/:id`, { id }),
  create: () => buildEndpoint(GROUP_BASE),
  update: (id: ID) => buildEndpointWithParams(`${GROUP_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${GROUP_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${GROUP_BASE}/validate/code/:code`, { code }),
  nextCode: () => buildEndpoint(`${GROUP_BASE}/next-code`),
} as const;

const LEDGER_BASE = '/coa-ledgers';
export const coaLedgerEndpoints = {
  list: () => buildEndpoint(LEDGER_BASE),
  natures: () => buildEndpoint(`${LEDGER_BASE}/natures`),
  getById: (id: ID) => buildEndpointWithParams(`${LEDGER_BASE}/:id`, { id }),
  /** ADR-0027 ③c: the ledger's BU availability whitelist (empty = all branches). */
  buAccess: (id: ID) => buildEndpointWithParams(`${LEDGER_BASE}/:id/bu-access`, { id }),
  create: () => buildEndpoint(LEDGER_BASE),
  update: (id: ID) => buildEndpointWithParams(`${LEDGER_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${LEDGER_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${LEDGER_BASE}/validate/code/:code`, { code }),
  /** ADR-0061: the next structured code in a subtype's block. */
  nextCode: () => buildEndpoint(`${LEDGER_BASE}/next-code`),
} as const;

const VOUCHER_TYPE_BASE = '/voucher-types';
export const voucherTypeEndpoints = {
  list: () => buildEndpoint(VOUCHER_TYPE_BASE),
  classes: () => buildEndpoint(`${VOUCHER_TYPE_BASE}/classes`),
  getById: (id: ID) => buildEndpointWithParams(`${VOUCHER_TYPE_BASE}/:id`, { id }),
  create: () => buildEndpoint(VOUCHER_TYPE_BASE),
  update: (id: ID) => buildEndpointWithParams(`${VOUCHER_TYPE_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${VOUCHER_TYPE_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${VOUCHER_TYPE_BASE}/validate/code/:code`, { code }),
} as const;

const JOURNAL_ENTRY_BASE = '/journal-entries';
export const journalEntryEndpoints = {
  list: () => buildEndpoint(JOURNAL_ENTRY_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${JOURNAL_ENTRY_BASE}/:id`, { id }),
  create: () => buildEndpoint(JOURNAL_ENTRY_BASE),
  update: (id: ID) => buildEndpointWithParams(`${JOURNAL_ENTRY_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${JOURNAL_ENTRY_BASE}/:id`, { id }),
  outstandingItems: () => buildEndpoint(`${JOURNAL_ENTRY_BASE}/outstanding-items`),
  ledgerBalance: () => buildEndpoint(`${JOURNAL_ENTRY_BASE}/ledger-balance`),
} as const;

const REPORT_BASE = '/reports';
export const reportEndpoints = {
  dayBook: () => buildEndpoint(`${REPORT_BASE}/day-book`),
  ledger: () => buildEndpoint(`${REPORT_BASE}/ledger`),
  openBills: () => buildEndpoint(`${REPORT_BASE}/open-bills`),
  debtorsAgeing: () => buildEndpoint(`${REPORT_BASE}/debtors-ageing`),
  trialBalance: () => buildEndpoint(`${REPORT_BASE}/trial-balance`),
  trialBalanceLedger: () => buildEndpoint(`${REPORT_BASE}/trial-balance/ledger`),
  trialBalanceVoucher: (id: string) => buildEndpoint(`${REPORT_BASE}/trial-balance/voucher/${id}`),
  trialBalanceExport: () => buildEndpoint(`${REPORT_BASE}/trial-balance/export`),
  arOutstanding: () => buildEndpoint(`${REPORT_BASE}/ar-outstanding`),
  arOutstandingLedger: () => buildEndpoint(`${REPORT_BASE}/ar-outstanding/ledger`),
  itemSales: () => buildEndpoint(`${REPORT_BASE}/item-sales`),
  itemSalesDetail: () => buildEndpoint(`${REPORT_BASE}/item-sales/detail`),
  itemSalesExport: () => buildEndpoint(`${REPORT_BASE}/item-sales/export`),
  taxSummary: () => buildEndpoint(`${REPORT_BASE}/tax-summary`),
  taxSummaryExport: () => buildEndpoint(`${REPORT_BASE}/tax-summary/export`),
  dayBookExport: () => buildEndpoint(`${REPORT_BASE}/day-book/export`),
  ledgerExport: () => buildEndpoint(`${REPORT_BASE}/ledger/export`),
  openBillsExport: () => buildEndpoint(`${REPORT_BASE}/open-bills/export`),
  debtorsAgeingExport: () => buildEndpoint(`${REPORT_BASE}/debtors-ageing/export`),
  arOutstandingExport: () => buildEndpoint(`${REPORT_BASE}/ar-outstanding/export`),
} as const;

const PERIOD_LOCK_BASE = '/period-locks';
export const periodLockEndpoints = {
  list: () => buildEndpoint(PERIOD_LOCK_BASE),
  create: () => buildEndpoint(PERIOD_LOCK_BASE),
  unlock: (id: ID) => buildEndpointWithParams(`${PERIOD_LOCK_BASE}/:id`, { id }),
} as const;

const ENTITY_CONFIG_BASE = '/entity-accounting-config';
export const entityAccountingConfigEndpoints = {
  config: () => buildEndpoint(ENTITY_CONFIG_BASE),
  ledgerDefaults: () => buildEndpoint(`${ENTITY_CONFIG_BASE}/ledger-defaults`),
  clearLedgerDefault: (purposeCode: string) => buildEndpointWithParams(`${ENTITY_CONFIG_BASE}/ledger-defaults/:purposeCode`, { purposeCode }),
  purposes: () => buildEndpoint(`${ENTITY_CONFIG_BASE}/purposes`),
} as const;

// --- Unified Payment Voucher (ADR-0039) ---
const PAYMENT_BASE = '/payments';
export const paymentEndpoints = {
  list: () => buildEndpoint(PAYMENT_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${PAYMENT_BASE}/:id`, { id }),
  create: () => buildEndpoint(PAYMENT_BASE),
  cancel: (id: ID) => buildEndpointWithParams(`${PAYMENT_BASE}/:id`, { id }),
  openBills: () => buildEndpoint(`${PAYMENT_BASE}/open-bills`),
  payableParties: () => buildEndpoint(`${PAYMENT_BASE}/payable-parties`),
  deductionReasons: () => buildEndpoint(`${PAYMENT_BASE}/deduction-reasons`),
} as const;

// --- Receipt Voucher — one-step receipt (ADR-0054) ---
const RECEIPT_VOUCHER_BASE = '/receipt-vouchers';
export const receiptVoucherEndpoints = {
  list: () => buildEndpoint(RECEIPT_VOUCHER_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${RECEIPT_VOUCHER_BASE}/:id`, { id }),
  create: () => buildEndpoint(RECEIPT_VOUCHER_BASE),
  cancel: (id: ID) => buildEndpointWithParams(`${RECEIPT_VOUCHER_BASE}/:id`, { id }),
  openItems: () => buildEndpoint(`${RECEIPT_VOUCHER_BASE}/open-items`),
  receivableParties: () => buildEndpoint(`${RECEIPT_VOUCHER_BASE}/receivable-parties`),
  deductionReasons: () => buildEndpoint(`${RECEIPT_VOUCHER_BASE}/deduction-reasons`),
} as const;

// --- Outstanding display (ADR-0042 §6) ---
const OUTSTANDING_BASE = '/outstanding';
export const outstandingEndpoints = {
  /**
   * `businessUnitId` names the unit the near figure should describe. Omit it to use the caller's
   * session unit; pass it where the work on screen belongs to another unit (receipt allocation).
   */
  party: (partyId: ID, businessUnitId?: ID | null) => {
    const base = buildEndpointWithParams(`${OUTSTANDING_BASE}/party/:partyId`, { partyId });
    return businessUnitId ? buildEndpointWithQuery(base, { businessUnitId }) : base;
  },
} as const;
